import { useState } from "react";
import useSWR from "swr";
import { Plus, Save, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { compute } from "@/lib/finance";
import { pctFrac, mult } from "@/lib/format";
import { Card, btn, inputCls } from "@/components/common/Ornament";
import FinancialForm from "@/components/financial/FinancialForm";

function Editor({ preset, onSaved, onCancel }) {
  const [name, setName] = useState(preset.name);
  const [vals, setVals] = useState({ ...preset.inputs });
  const [busy, setBusy] = useState(false);
  const r = compute(vals);
  const save = async () => {
    setBusy(true);
    try {
      if (preset.id) await api.put(`/admin/presets/${preset.id}`, { name, inputs: vals });
      else await api.post("/admin/presets", { name, inputs: vals });
      toast.success("Preset saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="admin-preset-editor">
      <FinancialForm values={vals} onChange={setVals} prefix="admin-preset" />
      <div className="lg:sticky lg:top-6 paper rounded-lg p-6 space-y-4">
        <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Preset name" data-testid="admin-preset-name-input" />
        <p className="text-sm text-slate-600">Levered IRR <strong className="font-mono-num text-[#0F2E23]">{pctFrac(r.leveredIRR)}</strong> · Equity multiple <strong className="font-mono-num text-[#0F2E23]">{mult(r.equityMultiple)}</strong></p>
        <div className="flex gap-2">
          <button onClick={save} disabled={busy || !name.trim()} className={btn.gold} data-testid="admin-preset-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save preset</button>
          <button onClick={onCancel} className={btn.ghost} data-testid="admin-preset-cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function PresetsTab() {
  const { data, mutate } = useSWR("/financials");
  const [editing, setEditing] = useState(null);
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" />;

  const del = async (p) => {
    if (!window.confirm(`Delete preset “${p.name}”?`)) return;
    await api.delete(`/admin/presets/${p.id}`); mutate(); toast.success("Preset deleted");
  };

  if (editing) return <Editor key={editing.id || "new"} preset={editing} onSaved={() => { mutate(); setEditing(null); }} onCancel={() => setEditing(null)} />;

  return (
    <Card eyebrow="One-Click Scenarios" title="Preset Scenarios" testId="admin-presets"
      right={<button onClick={() => setEditing({ name: "", inputs: data.baseline })} className={btn.primary + " !h-9"} data-testid="admin-preset-add-btn"><Plus className="h-4 w-4" /> New from baseline</button>}>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.presets.map((p, i) => {
          const r = compute(p.inputs);
          return (
            <div key={p.id} className="rounded-lg border border-[#E2DCD2] p-5 bg-[#F9F6F0]" data-testid={`admin-preset-card-${i}`}>
              <h3 className="font-display text-2xl text-[#0F2E23] font-semibold">{p.name}</h3>
              <p className="text-xs text-slate-600 mt-2 font-mono-num">ADR ${p.inputs.adr} · Occ {p.inputs.occupancy}% · Cap {p.inputs.exit_cap}%</p>
              <p className="text-sm mt-1">Levered IRR <strong className="font-mono-num">{pctFrac(r.leveredIRR)}</strong></p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditing(p)} className={btn.ghost + " !h-9"} data-testid={`admin-preset-edit-${i}`}>Edit</button>
                <button onClick={() => del(p)} className={btn.danger} data-testid={`admin-preset-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
